import type { PiiTier, PiiType } from "../types";

/**
 * Redaction placeholders per PII type (Danish-facing tokens).
 *
 * Part of the shared, data-driven ruleset (plan decision 7). Kept as plain data
 * so the Python document worker (LAR-143) can consume an identical mapping when
 * this file is promoted to the repo-root `shared/pii-ruleset/`.
 *
 * `custom` spans use their user-supplied label instead (see `redact.ts`); this
 * entry is only the fallback when no label was given.
 */
export const PII_PLACEHOLDERS: Record<PiiType, string> = {
  cpr: "[CPR]",
  phone: "[TELEFON]",
  email: "[EMAIL]",
  account: "[KONTONR]",
  address: "[ADRESSE]",
  name: "[NAVN]",
  org: "[ORG]",
  custom: "[FØLSOM]",
};

/**
 * Sensitivity tier per type. `hard` = deterministic formatted identifiers that
 * the whitelist can never suppress and whose override is always audited.
 */
export const PII_TIERS: Record<PiiType, PiiTier> = {
  cpr: "hard",
  phone: "hard",
  email: "hard",
  account: "hard",
  address: "hard",
  name: "soft",
  org: "soft",
  custom: "soft",
};

/**
 * Build the placeholder token for a span (plan decision 4).
 *
 * `index` (a span's `entityId`) makes the token numbered so repeated/coreferent
 * mentions of one entity read consistently: `placeholderFor("name", 1)` →
 * `[NAVN1]`. Omit it for the legacy un-numbered token (`[NAVN]`). A `custom`
 * span uses its user-supplied label as the base (`[DIAGNOSE1]`); the mapping
 * entry is only the fallback when no label was given.
 */
export function placeholderFor(
  type: PiiType,
  index?: number,
  label?: string
): string {
  const base =
    type === "custom" && label && label.trim().length > 0
      ? label.trim().toUpperCase()
      : PII_PLACEHOLDERS[type].slice(1, -1); // strip the surrounding [ ]
  return index === undefined ? `[${base}]` : `[${base}${index}]`;
}
